import { getAuth } from 'https://www.gstatic.com/firebasejs/9.23.0/firebase-auth.js';
import { 
    getFirestore, 
    doc, 
    getDoc, 
    addDoc, 
    deleteDoc, 
    updateDoc, 
    collection, 
    query, 
    where, 
    getDocs, 
    serverTimestamp,
    onSnapshot,
    arrayUnion,
    arrayRemove,
    orderBy,
    limit,
    collectionGroup,
    increment
} from 'https://www.gstatic.com/firebasejs/9.23.0/firebase-firestore.js';
import { getFunctions, httpsCallable } from 'https://www.gstatic.com/firebasejs/9.23.0/firebase-functions.js';
import { getApp } from 'https://www.gstatic.com/firebasejs/9.23.0/firebase-app.js';
import { getEstimatedServerTime } from '../timeSync.js';
import { getUserProfile } from './firestoreUser.js';

function getDailyResetPeriodStart() {
    const now = new Date(getEstimatedServerTime());
    const reset = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 7, 0, 0, 0));
    if (now.getTime() < reset.getTime()) {
        reset.setUTCDate(reset.getUTCDate() - 1);
    }
    return reset.getTime();
}

async function callCloudFunction(functionName, data) {
    const functions = getFunctions(getApp(), 'us-east1');
    const callable = httpsCallable(functions, functionName);
    try {
        const result = await callable(data);
        return result.data;
    } catch (error) {
        console.error(`[CloudFunction] ${functionName} error:`, error);
        throw error;
    }
}

export async function getAllGenreStandards() {
    const db = getFirestore();
    const standardsCol = collection(db, 'genre_standards');
    const snapshot = await getDocs(standardsCol);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
}

export async function findGenreStandard(genreName) {
    if (!genreName) return null;
    const db = getFirestore();
    const genreAliasMap = { 'rap': 'hip-hop', 'edm': 'electronic' };
    let correctedGenre = genreAliasMap[genreName.toLowerCase()] || genreName;
    let standardRef = doc(db, 'genre_standards', correctedGenre);
    let docSnap = await getDoc(standardRef);
    if (!docSnap.exists()) {
        const normalizedGenre = correctedGenre.toLowerCase().replace(/[- ]/g, '');
        const standardsSnapshot = await getDocs(collection(db, 'genre_standards'));
        for (const doc of standardsSnapshot.docs) {
            const standardId = doc.id.toLowerCase().replace(/[- ]/g, '');
            if (standardId === normalizedGenre) { docSnap = doc; correctedGenre = doc.id; break; }
        }
    }
    return docSnap.exists() ? { name: correctedGenre, ...docSnap.data() } : null;
}

export async function getGenreStandard(genreName) {
    if (!genreName) return null;
    const db = getFirestore();
    const standardRef = doc(db, 'genre_standards', genreName);
    const docSnap = await getDoc(standardRef);
    return docSnap.exists() ? docSnap.data() : null;
}

export async function updateSong(songId, dataToUpdate) {
    const db = getFirestore();
    const songRef = doc(db, 'songs', songId);
    const user = getAuth().currentUser;
    
    
    const songSnap = await getDoc(songRef);
    const songUserId = songSnap.exists() ? songSnap.data().userId : 'NOT_FOUND';
    console.log('[DEBUG] updateSong called:', { songId, userId: user?.uid, songOwnerId: songUserId, match: user?.uid === songUserId, dataToUpdate });
    
    try {
        const result = await updateDoc(songRef, dataToUpdate);
        console.log('[DEBUG] updateSong success, verifying data...');
        
        
        const verifySnap = await getDoc(songRef);
        if (verifySnap.exists()) {
            const savedData = verifySnap.data();
            console.log('[DEBUG] Saved data keys:', Object.keys(savedData));
            console.log('[DEBUG] albumArtUrl in saved data:', savedData.albumArtUrl ? 'YES' : 'NO');
            console.log('[DEBUG] imageUrl in saved data:', savedData.imageUrl ? 'YES' : 'NO');
            if (savedData.imageUrl) {
                console.log('[DEBUG] Saved imageUrl:', savedData.imageUrl);
            }
        }
        
        return result;
    } catch (error) {
        console.error('[DEBUG] updateSong error:', error.code, error.message);
        throw error;
    }
}

export async function saveAnimationData(songId, animationData) {
    const db = getFirestore();
    const songRef = doc(db, 'songs', songId);
    return updateDoc(songRef, { animationData });
}

export async function saveSongMetadata(userId, songData) {
    const db = getFirestore();
    
    const songs = await getUserSongs(userId);
    if (songs.length >= 15) {
        throw new Error('Maximum song limit (15) reached. Please delete a song before uploading more.');
    }
    return await addDoc(collection(db, 'songs'), {
        ...songData, userId: userId, uploadedAt: serverTimestamp(), likedBy: [], plays: 0
    });
}


export async function incrementSongPlays(songId, songOwnerId, playerUserId = null) {
    console.log('[incrementSongPlays] Calling cloud function for:', songId);
    try {
        const functions = getFunctions(getApp(), 'us-east1');
        const recordSongPlay = httpsCallable(functions, 'recordSongPlay');
        const result = await recordSongPlay({ songId, songOwnerId, playerUserId });
        console.log('[incrementSongPlays] Success:', result.data);
    } catch (error) {
        console.error('[incrementSongPlays] Cloud function failed:', error);
        
    }
}


export async function getAggregatedSongStats(userId) {
    const songs = await getUserSongs(userId);
    let totalPlays = 0;
    let totalLikes = 0;
    let totalComments = 0;
    
    songs.forEach(song => {
        totalPlays += song.plays || 0;
        totalLikes += (song.likedBy?.length || 0);
        totalComments += (song.commentCount || 0);
    });
    
    return { totalPlays, totalLikes, totalComments };
}


const FEEDBACK_SECTIONS = ['soundDesign', 'arrangement', 'creativity', 'mixQuality', 'catchiness'];

function feedbackDocScore(ratings) {
    if (!ratings) return 0;
    let total = 0;
    let count = 0;
    FEEDBACK_SECTIONS.forEach(section => {
        if (ratings[section] > 0) {
            total += ratings[section];
            count++;
        }
    });
    return count > 0 ? total / count : 0;
}

export async function getSongFeedbackStats(songId) {
    if (!songId) return { averageRating: 0, feedbackCount: 0 };
    const db = getFirestore();
    const feedbackCol = collection(db, `songs/${songId}/feedback`);
    const snapshot = await getDocs(feedbackCol);
    
    let total = 0;
    let count = 0;
    snapshot.docs.forEach(doc => {
        const score = feedbackDocScore(doc.data()?.ratings);
        if (score > 0) {
            total += score;
            count++;
        }
    });
    
    return { averageRating: count > 0 ? parseFloat((total / count).toFixed(1)) : 0, feedbackCount: count };
}

export async function getSongCommentCount(songId, songOwnerId = null) {
    if (!songId) return 0;
    const db = getFirestore();
    const commentsCol = collection(db, `songs/${songId}/comments`);
    const snapshot = await getDocs(commentsCol);
    const topLevelComments = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).filter(c => {
        if (c.replyTo) return false;
        if (songOwnerId && c.userId === songOwnerId) return false;
        const text = c.commentText ? String(c.commentText).trim() : '';
        if (!text) return false;
        return true;
    });
    if (topLevelComments.length === 0) return 0;
    const userIds = [...new Set(topLevelComments.map(c => c.userId).filter(Boolean))];
    const profiles = await Promise.all(userIds.map(id => getUserProfile(id)));
    const validIds = new Set(profiles.filter(p => p).map(p => p.id));
    return topLevelComments.filter(c => validIds.has(c.userId)).length;
}

export async function getArtistFeedbackAverage(userId) {
    if (!userId) return { averageRating: 0, totalFeedback: 0 };
    const db = getFirestore();
    const songs = await getUserSongs(userId);
    let total = 0;
    let count = 0;
    
    await Promise.all(songs.map(async (song) => {
        if (!song.id) return;
        const feedbackCol = collection(db, `songs/${song.id}/feedback`);
        const snapshot = await getDocs(feedbackCol);
        snapshot.docs.forEach(doc => {
            const score = feedbackDocScore(doc.data()?.ratings);
            if (score > 0) {
                total += score;
                count++;
            }
        });
    }));
    
    return { averageRating: count > 0 ? parseFloat((total / count).toFixed(2)) : 0, totalFeedback: count };
}

export async function deleteSongMetadata(songId) {
    const db = getFirestore();
    const songRef = doc(db, 'songs', songId);
    return deleteDoc(songRef);
}

export async function getSongById(songId) {
    if (!songId) return null;
    const db = getFirestore();
    const songRef = doc(db, 'songs', songId);
    const docSnap = await getDoc(songRef);
    return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;
}

export async function getUserSongs(userId) {
    const db = getFirestore();
    const songsCol = collection(db, 'songs');
    const q = query(songsCol, where('userId', '==', userId));
    const songSnapshot = await getDocs(q);
    const allSongs = songSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    
    
    const completedSongs = allSongs.filter(song => song.waveformImageUrl && song.analysis);
    
    
    const userProfileRef = doc(db, 'users', userId);
    const userProfileSnap = await getDoc(userProfileRef);
    const userProfile = userProfileSnap.exists() ? userProfileSnap.data() : {};
    const songOrder = userProfile.songOrder || [];
    
    
    if (songOrder.length > 0) {
        const songMap = new Map(completedSongs.map(song => [song.id, song]));
        const orderedSongs = [];
        
        
        for (const songId of songOrder) {
            if (songMap.has(songId)) {
                orderedSongs.push(songMap.get(songId));
                songMap.delete(songId);
            }
        }
        
        
        orderedSongs.push(...Array.from(songMap.values()));
        
        return orderedSongs;
    }
    
    
    return completedSongs;
}

export async function getNewestSongByArtist(userId) {
    const db = getFirestore();
    const songsCol = collection(db, 'songs');
    
    
    const userProfileRef = doc(db, 'users', userId);
    const userProfileSnap = await getDoc(userProfileRef);
    const userProfile = userProfileSnap.exists() ? userProfileSnap.data() : {};
    const songOrder = userProfile.songOrder || [];
    
    
    const q = query(songsCol, where('userId', '==', userId));
    const songSnapshot = await getDocs(q);
    if (songSnapshot.empty) return null;
    
    
    const songMap = new Map();
    songSnapshot.docs.forEach(doc => {
        const song = { id: doc.id, ...doc.data() };
        
        if (song.waveformImageUrl && song.analysis) {
            songMap.set(doc.id, song);
        }
    });
    
    
    if (songOrder.length > 0) {
        for (const songId of songOrder) {
            if (songMap.has(songId)) {
                return songMap.get(songId);
            }
        }
    }
    
    
    const songs = Array.from(songMap.values());
    songs.sort((a, b) => {
        const dateA = a.uploadedAt?.toDate?.() || new Date(0);
        const dateB = b.uploadedAt?.toDate?.() || new Date(0);
        return dateB - dateA;
    });
    
    return songs[0] || null;
}

export async function getAllSongs() {
    const db = getFirestore();
    const songsCol = collection(db, 'songs');
    const songSnapshot = await getDocs(songsCol);
    return songSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
}

export async function getSongComments(songId) {
    const db = getFirestore();
    const commentsCol = collection(db, `songs/${songId}/comments`);
    const q = query(commentsCol);
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, songId, ...doc.data() }));
}

export async function getCommentsByUser(userId) {
    if (!userId) return [];
    const db = getFirestore();
    const commentsQuery = query(collectionGroup(db, 'comments'), where('userId', '==', userId), orderBy('timestamp', 'desc'));
    const commentsSnapshot = await getDocs(commentsQuery);
    if (commentsSnapshot.empty) return [];
    const comments = commentsSnapshot.docs.map(doc => ({ id: doc.id, songId: doc.ref.parent.parent.id, ...doc.data() }));
    const songIds = [...new Set(comments.map(c => c.songId))];
    const songs = {};
    if (songIds.length > 0) {
        const songsQuery = query(collection(db, 'songs'), where('__name__', 'in', songIds));
        const songsSnapshot = await getDocs(songsQuery);
        songsSnapshot.forEach(doc => { songs[doc.id] = doc.data().title || 'Untitled'; });
    }
    return comments.map(comment => ({ ...comment, songTitle: songs[comment.songId] || 'Unknown Song' }));
}


export async function saveComment(songId, commentData, commentId = null) {
    try {
        const result = await callCloudFunction('saveCommentWithStats', {
            songId,
            commentData,
            commentId
        });
        return result.commentId;
    } catch (error) {
        
        console.warn('[saveComment] Cloud function failed, falling back to direct write:', error.message);
        const db = getFirestore();
        const commentsCol = collection(db, `songs/${songId}/comments`);
        if (commentId) {
            const commentRef = doc(db, `songs/${songId}/comments`, commentId);
            await updateDoc(commentRef, commentData);
            return commentId;
        } else {
            const docRef = await addDoc(commentsCol, commentData);
            return docRef.id;
        }
    }
}


export async function saveFeedback(songId, feedbackData) {
    try {
        const db = getFirestore();
        const feedbackCol = collection(db, `songs/${songId}/feedback`);
        const docRef = await addDoc(feedbackCol, {
            ...feedbackData,
            timestamp: Date.now()
        });
        return docRef.id;
    } catch (error) {
        console.error('[saveFeedback] Error saving feedback:', error);
        throw error;
    }
}


export async function deleteComment(songId, commentId) {
    try {
        await callCloudFunction('deleteCommentWithStats', {
            songId,
            commentId
        });
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('badge:comment-deleted'));
        }
        try {
            const { getAuth } = await import('https://www.gstatic.com/firebasejs/9.23.0/firebase-auth.js');
            const user = getAuth().currentUser;
            if (user) {
                const { getUserProfile } = await import('./firestoreService.js');
                const { recheckAllMilestones } = await import('../badgeState.js');
                const profile = await getUserProfile(user.uid);
                if (profile) await recheckAllMilestones(profile);
            }
        } catch (e) { console.error('[deleteComment] Badge recheck failed:', e); }
        return true;
    } catch (error) {
        
        console.warn('[deleteComment] Cloud function failed, falling back to direct delete:', error.message);
        const db = getFirestore();
        const commentRef = doc(db, `songs/${songId}/comments`, commentId);
        return deleteDoc(commentRef);
    }
}

export async function toggleLikeStatus(songId, userId, skipWarning = false) {
    const db = getFirestore();
    const songRef = doc(db, 'songs', songId);
    const docSnap = await getDoc(songRef);
    if (!docSnap.exists()) throw new Error("Song not found");
    const songData = docSnap.data();
    const likedBy = songData.likedBy || [];
    let isLiked;
    const resetPeriodStart = getDailyResetPeriodStart();
    
    if (likedBy.includes(userId)) {
        
        await updateDoc(songRef, { likedBy: arrayRemove(userId) });
        isLiked = false;
        
        await trackUserSongsLiked(userId, false, resetPeriodStart);
        
        
    } else {
        console.log('[toggleLikeStatus] LIKING song - songId:', songId, 'userId:', userId, 'owner:', songData.userId);
        await updateDoc(songRef, { likedBy: arrayUnion(userId) });
        isLiked = true;
        
        await trackUserSongsLiked(userId, true, resetPeriodStart);
        
        
        if (songData.userId !== userId) {
            await addToDailyUniqueLikes(userId, songId);
        }
        
        if (songData.userId !== userId) {
            await addToDailyUniqueLikesReceived(songData.userId, songId);
        }
    }
    return isLiked;
}


async function trackUserSongsLiked(userId, isLiking, resetPeriodStart) {
    try {
        const { getFirestore, doc, getDoc, updateDoc, increment, serverTimestamp } = await import('https://www.gstatic.com/firebasejs/9.23.0/firebase-firestore.js');
        const db = getFirestore();
        const userRef = doc(db, 'users', userId);
        const delta = isLiking ? 1 : -1;
        
        const docSnap = await getDoc(userRef);
        const stats = docSnap.exists() ? (docSnap.data().stats || {}) : {};
        const lastLikeUpdate = stats.lastLikeUpdate?.toMillis?.() || 0;
        const isStale = !lastLikeUpdate || lastLikeUpdate < resetPeriodStart;
        
        const updateObj = {
            'stats.songsLiked': increment(delta),
            'stats.lastLikeUpdate': serverTimestamp()
        };
        
        if (isStale) {
            updateObj['stats.dailyLikesSentToday'] = isLiking ? 1 : 0;
        } else {
            updateObj['stats.dailyLikesSentToday'] = increment(delta);
        }
        
        await updateDoc(userRef, updateObj);
        
        if (isLiking) {
            window.dispatchEvent(new CustomEvent('badge:song-liked'));
        }
    } catch (error) {
        console.error("Error tracking user songsLiked:", error);
    }
}


async function addToDailyUniqueLikes(userId, songId) {
    try {
        
        window._pendingDailyLikes = window._pendingDailyLikes || {};
        window._pendingDailyLikes[userId] = window._pendingDailyLikes[userId] || [];
        if (window._pendingDailyLikes[userId].includes(songId)) {
            console.log('[DailyLikes] Song already in pending cache (this session), skipping:', songId);
            return;
        }

        
        
        const { getFirestore, doc, getDoc } = await import('https://www.gstatic.com/firebasejs/9.23.0/firebase-firestore.js');
        const db = getFirestore();
        const userRef = doc(db, 'users', userId);
        const docSnap = await getDoc(userRef);
        
        if (!docSnap.exists()) {
            console.log('[DailyLikes] User profile not found:', userId);
            return;
        }
        
        const profile = docSnap.data();
        const dailyUniqueLikes = profile.preferences?.dailyUniqueLikesGiven || [];
        const dailySongsUsedForCoins = profile.preferences?.dailySongsUsedForCoins || [];
        
        
        if (dailySongsUsedForCoins.includes(songId)) {
            console.log('[DailyLikes] Song already used for coins today, skipping:', songId);
            
            const { updateScrollingNotificationText } = await import('../notificationsManager.js');
            updateScrollingNotificationText('Already earned coins for Appreciator badge.', 'coin', 'fa-coins');
            return;
        }
        
        
        if (dailyUniqueLikes.length >= 3) {
            console.log('[DailyLikes] Daily limit reached (3 coins), skipping:', songId);
            return;
        }
        
        
        window._pendingDailyLikes[userId].push(songId);
        console.log('[DailyLikes] Added to pending cache BEFORE save:', songId, 'cache:', window._pendingDailyLikes[userId]);
        
        
        const updatedLikes = [...dailyUniqueLikes, songId];
        const updatedUsedCoins = [...dailySongsUsedForCoins, songId];
        
        
        const { saveUserProfile } = await import('./firestoreService.js');
        await saveUserProfile(userId, { 
            'preferences.dailyUniqueLikesGiven': updatedLikes,
            'preferences.dailySongsUsedForCoins': updatedUsedCoins
        });
        console.log('[DailyLikes] Added song to dailyUniqueLikesGiven:', songId, 'Total used for coins today:', updatedUsedCoins.length);
        
        
        window.dispatchEvent(new CustomEvent('badge:song-liked'));
    } catch (error) {
        console.error("Error adding to daily unique likes:", error);
    }
}


async function addToDailyUniqueLikesReceived(songOwnerId, songId) {
    try {
        
        const functions = getFunctions(getApp(), 'us-east1');
        const updateDailyLikesReceived = httpsCallable(functions, 'updateDailyLikesReceived');
        const result = await updateDailyLikesReceived({ songOwnerId, songId, action: 'add' });
        
        console.log('[DailyLikes] Cloud function result:', result.data);
        
        if (result.data.success) {
            
            console.log('[DailyLikes] Dispatching badge:liked-received event');
            window.dispatchEvent(new CustomEvent('badge:liked-received'));
        } else {
            console.log('[DailyLikes] Cloud function did not return success, skipping badge event dispatch');
        }
    } catch (error) {
        console.error("Error adding to daily unique likes received:", error);
    }
}


async function removeFromDailyUniqueLikesReceived(songOwnerId, songId) {
    try {
        
        const functions = getFunctions(getApp(), 'us-east1');
        const updateDailyLikesReceived = httpsCallable(functions, 'updateDailyLikesReceived');
        const result = await updateDailyLikesReceived({ songOwnerId, songId, action: 'remove' });
        
        console.log('[DailyLikes] Cloud function result:', result.data);
        
        if (result.data.success) {
            
            console.log('[DailyLikes] Removed from dailyUniqueLikesReceived:', songId);
        }
    } catch (error) {
        console.error("Error removing from daily unique likes received:", error);
    }
}

export function listenForSongUpdates(songId, callback) {
    const db = getFirestore();
    const songRef = doc(db, 'songs', songId);
    const unsubscribe = onSnapshot(songRef, (docSnap) => {
        if (docSnap.exists()) { callback({ id: docSnap.id, ...docSnap.data() }); }
    });
    return unsubscribe;
}